"use client";

import { useState, useEffect } from "react";
import AppLogo from "@/components/ui/AppLogo";

const navLinks = [
  { label: "Services", href: "#services" },
  { label: "Why Beecon", href: "#about" },
  { label: "Areas", href: "#areas" },
  { label: "Testimonials", href: "#testimonials" },
  { label: "Contact", href: "#contact" },
];

export default function NavHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
        style={{
          padding: scrolled ? '8px 0' : '16px 0',
          background: scrolled
            ? 'rgba(6, 21, 48, 0.95)'
            : 'transparent',
          backdropFilter: scrolled ? 'blur(20px)' : 'none',
          borderBottom: scrolled ? '1px solid rgba(201,168,76,0.15)' : 'none',
        }}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <AppLogo
              text="Beecon"
              iconName="HeartIcon"
              size={64}
              className="text-white"
            />
          </div>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8" aria-label="Main navigation">
            {navLinks.map((link) => (
              <button
                key={`nav-${link.label}`}
                onClick={() => handleNavClick(link.href)}
                className="text-sm font-medium font-sans transition-all duration-200 hover:opacity-100 text-white/65 hover:text-[var(--gold)]"
                aria-label={`Navigate to ${link.label}`}
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://wa.me/256788217719"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold px-5 py-2.5 text-sm font-bold flex items-center gap-2"
              aria-label="Book a nurse via WhatsApp"
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Book a Nurse
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden text-white p-2"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
          >
            <div className="w-6 flex flex-col gap-1.5 items-end">
              <span className="block h-0.5 bg-current transition-all duration-300" style={{ width: menuOpen ? '100%' : '100%', transform: menuOpen ? 'rotate(45deg) translateY(8px)' : 'none' }} />
              <span className="block h-0.5 bg-current transition-all duration-300" style={{ width: '70%', opacity: menuOpen ? 0 : 1 }} />
              <span className="block h-0.5 bg-current transition-all duration-300" style={{ width: '100%', transform: menuOpen ? 'rotate(-45deg) translateY(-8px)' : 'none' }} />
            </div>
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      <div
        className="fixed inset-0 z-40 md:hidden transition-all duration-500"
        style={{
          opacity: menuOpen ? 1 : 0,
          pointerEvents: menuOpen ? 'auto' : 'none',
          background: 'rgba(6,21,48,0.97)',
          backdropFilter: 'blur(20px)',
        }}
        aria-hidden={!menuOpen}
      >
        <div
          className="flex flex-col justify-center items-center h-full gap-8"
          style={{ transform: menuOpen ? 'translateY(0)' : 'translateY(-20px)', transition: 'transform 0.5s cubic-bezier(0.16,1,0.3,1)' }}
        >
          {navLinks.map((link) => (
            <button
              key={`mobile-nav-${link.label}`}
              onClick={() => handleNavClick(link.href)}
              className="font-serif text-3xl font-light text-white/70 hover:text-white transition-colors"
            >
              {link.label}
            </button>
          ))}
          <a
            href="https://wa.me/256788217719"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-gold px-8 py-4 text-base font-bold mt-4"
            onClick={() => setMenuOpen(false)}
          >
            Book a Nurse Today
          </a>
        </div>
      </div>
    </>
  );
}